/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWallet } from '../../hooks/useWallet';
import TopUpCard from '../../components/billing/TopUpCard';
import { AutoPayCard, PromoCodeCard, SavedCardsCard } from '../../components/billing/PaymentMethodCards';
import RefundModal from '../../components/billing/RefundModal';
import { useBillingContext, usePaymentMethods } from '../../components/billing/useBilling';

/**
 * Add funds & cards. Deep links (stripped after use):
 *   ?action=topup  → focus the amount field
 *   ?action=card   → open "Add card"
 *   ?action=coupon → focus the promo code field
 *   ?action=refund → open the refund request
 * PayPal return params (?payment=…&token=…) are handled by BillingLayout.
 */
const BillingPayments: React.FC = () => {
  const { exchangeRate } = useBillingContext();
  const { wallet } = useWallet();
  const { cards, isLoading: cardsLoading, refetch } = usePaymentMethods();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mandateOpen, setMandateOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const promoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const action = searchParams.get('action');
    if (!action) return;
    const focus = (el: HTMLInputElement | null) => {
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    };
    if (action === 'topup') focus(amountRef.current);
    else if (action === 'coupon') focus(promoRef.current);
    else if (action === 'card') setMandateOpen(true);
    else if (action === 'refund') setRefundOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="flex flex-wrap gap-5 items-start">
      <TopUpCard
        ref={amountRef}
        balance={wallet?.balance || 0}
        exchangeRate={exchangeRate}
        hourlyRunRate={wallet?.has_running_resources ? wallet.hourly_run_rate || 0 : 0}
      />
      <div className="flex-[2_1_320px] min-w-0 flex flex-col gap-5">
        <SavedCardsCard
          wallet={wallet}
          cards={cards}
          isLoading={cardsLoading}
          refetch={refetch}
          mandateOpen={mandateOpen}
          setMandateOpen={setMandateOpen}
        />
        <AutoPayCard wallet={wallet} cards={cards} onAddCard={() => setMandateOpen(true)} />
        <PromoCodeCard ref={promoRef} onRefund={() => setRefundOpen(true)} />
      </div>
      <RefundModal open={refundOpen} onClose={() => setRefundOpen(false)} />
    </div>
  );
};

export default BillingPayments;
